import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Save, DollarSign, Plus, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Default currencies
const DEFAULT_CURRENCIES = [
  { code: 'COP', name: 'Peso Colombiano', symbol: '$' },
  { code: 'CLP', name: 'Peso Chileno', symbol: '$' },
  { code: 'USD', name: 'Dólar Estadounidense', symbol: '$' }
];

// Format currency for display
const formatCurrency = (amount, currencyCode) => {
  const formatted = new Intl.NumberFormat('es-CO', { 
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
  return `$${formatted} ${currencyCode}`;
};

export default function AdminMembershipConfig() {
  const navigate = useNavigate();
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [currencies, setCurrencies] = useState(DEFAULT_CURRENCIES);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('konemed_admin_token');
      const [typesRes, currenciesRes] = await Promise.all([
        axios.get(`${API_URL}/admin/membership-types`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/admin/currencies`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: DEFAULT_CURRENCIES }))
      ]);
      
      // Ensure each type has precios array
      const typesWithPrecios = typesRes.data.map(type => ({
        ...type,
        precios: type.precios && type.precios.length > 0 
          ? type.precios 
          : DEFAULT_CURRENCIES.map(c => ({ currency_code: c.code, amount: type.valor || 0 }))
      }));
      
      setTypes(typesWithPrecios);
      if (currenciesRes.data) setCurrencies(currenciesRes.data);
    } catch (error) {
      toast.error('Error al cargar tipos de membresía');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (id, data) => {
    try {
      const token = localStorage.getItem('konemed_admin_token');
      const typeData = types.find(t => t.id === id);
      await axios.put(`${API_URL}/admin/membership-types/${id}`, {
        cantidad_consentimientos: data.cantidad_consentimientos,
        valor: data.precios.find(p => p.currency_code === 'COP')?.amount || 0,
        precios: data.precios,
        codigo_firma_habilitado: typeData?.codigo_firma_habilitado || false
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Membresía actualizada');
      setEditingId(null);
      fetchData();
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const handleToggleCodigoFirma = (id, checked) => {
    setTypes(types.map(t => t.id === id ? { ...t, codigo_firma_habilitado: checked } : t));
  };

  const handleChangeConsents = (id, value) => {
    setTypes(types.map(t => t.id === id ? { ...t, cantidad_consentimientos: parseInt(value) } : t));
  };

  const handlePriceChange = (typeId, currencyCode, value) => {
    setTypes(types.map(t => {
      if (t.id !== typeId) return t;
      
      const newPrecios = t.precios.map(p => 
        p.currency_code === currencyCode 
          ? { ...p, amount: parseFloat(value) || 0 }
          : p
      );
      
      return { ...t, precios: newPrecios };
    }));
  };

  const addCurrency = (typeId) => {
    const existingCurrencies = types.find(t => t.id === typeId)?.precios.map(p => p.currency_code) || [];
    const availableCurrencies = DEFAULT_CURRENCIES.filter(c => !existingCurrencies.includes(c.code));
    
    if (availableCurrencies.length === 0) {
      toast.info('Ya están todas las monedas configuradas');
      return;
    }
    
    const newCurrency = availableCurrencies[0];
    setTypes(types.map(t => {
      if (t.id !== typeId) return t;
      return {
        ...t,
        precios: [...t.precios, { currency_code: newCurrency.code, amount: 0 }]
      };
    }));
  };

  const removeCurrency = (typeId, currencyCode) => {
    // Prevent removing all currencies
    const typePrecios = types.find(t => t.id === typeId)?.precios || [];
    if (typePrecios.length <= 1) {
      toast.error('Debe mantener al menos una moneda');
      return;
    }
    
    setTypes(types.map(t => {
      if (t.id !== typeId) return t;
      return {
        ...t,
        precios: t.precios.filter(p => p.currency_code !== currencyCode)
      };
    }));
  };

  const getCurrencyName = (code) => {
    const currency = currencies.find(c => c.code === code);
    return currency ? currency.name : code;
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-membership-config">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-8 py-6 flex items-center gap-4">
          <button onClick={() => navigate('/admin/dashboard')} className="p-2 hover:bg-slate-100 rounded-lg">
            <ArrowLeft size={24} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Configuración de Membresías</h1>
            <p className="text-slate-600">Configure precios por moneda para cada tipo de membresía</p>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-8 py-8">
        {/* Currency Legend */}
        <div className="bg-violet-50 border border-violet-200 rounded-xl p-4 mb-6">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="text-violet-600" size={20} />
            <h3 className="font-semibold text-violet-900">Monedas Disponibles</h3>
          </div>
          <div className="flex flex-wrap gap-4">
            {DEFAULT_CURRENCIES.map(currency => (
              <div key={currency.code} className="flex items-center gap-2">
                <span className="px-2 py-1 bg-violet-100 text-violet-700 rounded font-mono text-sm">
                  {currency.code}
                </span>
                <span className="text-sm text-violet-700">{currency.name}</span>
              </div>
            ))}
          </div>
          <p className="text-sm text-violet-600 mt-2">
            <strong>Colombia:</strong> COP | <strong>Chile:</strong> CLP | <strong>Otros países:</strong> USD
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
          </div>
        ) : (
          <div className="space-y-6">
            {types.map((type) => (
              <div key={type.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={`membership-type-${type.nombre}`}>
                <div className="bg-gradient-to-r from-violet-600 to-indigo-600 px-6 py-4 flex items-center justify-between">
                  <h3 className="text-xl font-bold text-white">{type.nombre}</h3>
                  {editingId === type.id ? (
                    <div className="flex gap-2">
                      <button 
                        onClick={() => setEditingId(null)} 
                        className="px-4 py-2 bg-white/20 text-white rounded-lg hover:bg-white/30 transition-colors"
                      >
                        Cancelar
                      </button>
                      <button 
                        onClick={() => handleUpdate(type.id, { cantidad_consentimientos: type.cantidad_consentimientos, precios: type.precios, codigo_firma_habilitado: type.codigo_firma_habilitado })} 
                        className="flex items-center gap-2 px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors"
                      >
                        <Save size={18} /> Guardar
                      </button>
                    </div>
                  ) : (
                    <button 
                      onClick={() => setEditingId(type.id)} 
                      className="px-4 py-2 bg-white/20 text-white rounded-lg hover:bg-white/30 transition-colors"
                    >
                      Editar
                    </button>
                  )}
                </div>
                
                <div className="p-6">
                  {/* Consents */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Cantidad de Consentimientos {type.cantidad_consentimientos === -1 && <span className="text-violet-600">(Ilimitado)</span>}
                    </label>
                    <input 
                      type="number" 
                      value={type.cantidad_consentimientos} 
                      onChange={(e) => handleChangeConsents(type.id, e.target.value)} 
                      disabled={editingId !== type.id} 
                      className="w-full max-w-xs px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 disabled:bg-slate-50"
                    />
                    <p className="text-xs text-slate-500 mt-1">Use -1 para consentimientos ilimitados</p>
                  </div>

                  {/* Prices per Currency */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <label className="block text-sm font-medium text-slate-700">
                        Precios por Moneda
                      </label>
                      {editingId === type.id && (
                        <button 
                          onClick={() => addCurrency(type.id)}
                          className="flex items-center gap-1 px-3 py-1 text-sm bg-violet-50 text-violet-600 hover:bg-violet-100 rounded-lg transition-colors"
                        >
                          <Plus size={16} /> Agregar Moneda
                        </button>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {type.precios.map((precio) => (
                        <div key={precio.currency_code} className="relative">
                          <div className={`border rounded-lg overflow-hidden ${editingId === type.id ? 'border-violet-200' : 'border-slate-200'}`}>
                            <div className="bg-slate-50 px-3 py-2 flex items-center justify-between">
                              <span className="font-semibold text-slate-700">{precio.currency_code}</span>
                              <span className="text-xs text-slate-500">{getCurrencyName(precio.currency_code)}</span>
                              {editingId === type.id && type.precios.length > 1 && (
                                <button
                                  onClick={() => removeCurrency(type.id, precio.currency_code)}
                                  className="p-1 text-red-500 hover:bg-red-50 rounded"
                                  title="Eliminar moneda"
                                >
                                  <X size={14} />
                                </button>
                              )}
                            </div>
                            <div className="p-3">
                              <div className="relative">
                                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400">$</span>
                                <input
                                  type="number"
                                  value={precio.amount}
                                  onChange={(e) => handlePriceChange(type.id, precio.currency_code, e.target.value)}
                                  disabled={editingId !== type.id}
                                  className="w-full pl-8 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 disabled:bg-slate-50 text-right font-mono"
                                  placeholder="0"
                                />
                              </div>
                              {editingId !== type.id && (
                                <p className="text-sm text-emerald-600 font-semibold mt-2 text-center">
                                  {formatCurrency(precio.amount, precio.currency_code)}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Código de Firma */}
                  <div className="mt-6 pt-6 border-t border-slate-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <label className="block text-sm font-medium text-slate-700">
                          Código de Firma
                        </label>
                        <p className="text-xs text-slate-500 mt-1">
                          Habilita que los centros con esta membresía puedan usar código de firma para pacientes presenciales
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={type.codigo_firma_habilitado || false}
                          onChange={(e) => handleToggleCodigoFirma(type.id, e.target.checked)}
                          disabled={editingId !== type.id}
                          data-testid={`toggle-codigo-firma-${type.nombre}`}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-violet-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-violet-600 peer-disabled:opacity-50"></div>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
